import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Upload, X, ArrowLeft, ImagePlus } from 'lucide-react';
import './SellProduct.css';

const CATEGORIES = [
  { value: 'notes',       label: '📒 Notes'       },
  { value: 'gaming',      label: '🎮 Gaming'      },
  { value: 'clothing',    label: '👕 Clothing'    },
  { value: 'electronics', label: '💻 Electronics' },
  { value: 'books',       label: '📚 Books'       },
  { value: 'sports',      label: '⚽ Sports'      },
  { value: 'food',        label: '🍱 Food'        },
  { value: 'other',       label: '📦 Other'       },
];

export default function SellProduct() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: '', description: '', category: '', price: '', quantity: '1',
  });
  const [images,        setImages]        = useState([]);   // preview URLs
  const [imageFiles,    setImageFiles]    = useState([]);   // File objects
  const [uploading,     setUploading]     = useState(false);
  const [submitting,    setSubmitting]    = useState(false);
  const [errors,        setErrors]        = useState({});

  // Dropzone
  const onDrop = useCallback((accepted) => {
    const remaining = 5 - images.length;
    const toAdd = accepted.slice(0, remaining);
    if (accepted.length > remaining) {
      toast.error(`Max 5 images. ${remaining} slot${remaining !== 1 ? 's' : ''} left.`);
    }
    toAdd.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImages(prev => [...prev, e.target.result]);
        setImageFiles(prev => [...prev, file]);
      };
      reader.readAsDataURL(file);
    });
  }, [images]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.webp'] },
    maxFiles: 5,
    disabled: images.length >= 5,
  });

  const removeImage = (idx) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
    setImageFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim())    e.title    = 'Title is required.';
    if (!form.description.trim()) e.description = 'Description is required.';
    if (!form.category)        e.category = 'Pick a category.';
    if (!form.price || isNaN(form.price) || Number(form.price) <= 0)
                               e.price    = 'Enter a valid price.';
    if (imageFiles.length === 0) e.images  = 'Add at least one photo.';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    try {
      // Step 1: upload images to Cloudinary
      setUploading(true);
      const formData = new FormData();
      imageFiles.forEach(f => formData.append('images', f));
      const { data: uploadData } = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploading(false);

      // Step 2: create product
      setSubmitting(true);
      await api.post('/products', {
        ...form,
        price:    Number(form.price),
        quantity: Number(form.quantity),
        images:   uploadData.urls,
      });

      toast.success('Listing created! 🎉');
      navigate('/app/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create listing.');
      setUploading(false);
      setSubmitting(false);
    }
  };

  const isLoading = uploading || submitting;

  return (
    <div className="sell-page fade-in">
      {/* Page header */}
      <div className="sell-header">
        <button className="btn btn-ghost btn-icon" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
        </button>
        <div>
          <h2>List an Item</h2>
          <p className="text-muted" style={{ fontSize: '0.875rem' }}>
            Fill in the details and upload photos
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="sell-form" noValidate>
        <div className="sell-grid">

          {/* Left col — images */}
          <div className="sell-images-col">
            <label className="label">Photos (up to 5)</label>

            {/* Dropzone */}
            {images.length < 5 && (
              <div
                {...getRootProps()}
                className={`dropzone ${isDragActive ? 'dropzone-active' : ''}`}
              >
                <input {...getInputProps()} />
                <ImagePlus size={28} className="dropzone-icon" />
                <p className="dropzone-text">
                  {isDragActive ? 'Drop here' : 'Drag photos or click to browse'}
                </p>
                <p className="dropzone-hint">JPG, PNG, WEBP · Max 5MB each</p>
              </div>
            )}

            {/* Preview grid */}
            {images.length > 0 && (
              <div className="image-preview-grid">
                {images.map((src, idx) => (
                  <div key={idx} className="image-preview-item">
                    <img src={src} alt={`Preview ${idx + 1}`} />
                    {idx === 0 && <span className="image-preview-main">Cover</span>}
                    <button
                      type="button"
                      className="image-preview-remove"
                      onClick={() => removeImage(idx)}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {errors.images && (
              <span className="error-text">{errors.images}</span>
            )}

            {/* Status */}
            {uploading && (
              <div className="sell-upload-status">
                <div className="spinner" />
                <span>Uploading photos...</span>
              </div>
            )}
          </div>

          {/* Right col — details */}
          <div className="sell-details-col">

            {/* Title */}
            <div className="form-group">
              <label className="label">Title</label>
              <input
                className={`input ${errors.title ? 'input-error' : ''}`}
                name="title"
                placeholder="e.g. Operating Systems notes (Sem 5)"
                value={form.title}
                onChange={handleChange}
                maxLength={100}
              />
              {errors.title && <span className="error-text">{errors.title}</span>}
            </div>

            {/* Description */}
            <div className="form-group">
              <label className="label">Description</label>
              <textarea
                className={`textarea ${errors.description ? 'input-error' : ''}`}
                name="description"
                placeholder="Describe condition, what's included, reason for selling..."
                value={form.description}
                onChange={handleChange}
                maxLength={1000}
                rows={4}
              />
              <span className="helper-text">{form.description.length}/1000</span>
              {errors.description && <span className="error-text">{errors.description}</span>}
            </div>

            {/* Category */}
            <div className="form-group">
              <label className="label">Category</label>
              <div className="category-grid">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat.value}
                    type="button"
                    className={`category-btn ${form.category === cat.value ? 'active' : ''}`}
                    onClick={() => {
                      setForm(f => ({ ...f, category: cat.value }));
                      setErrors(e => ({ ...e, category: '' }));
                    }}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
              {errors.category && <span className="error-text">{errors.category}</span>}
            </div>

            {/* Price + Quantity */}
            <div className="sell-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="label">Price (₹)</label>
                <input
                  className={`input ${errors.price ? 'input-error' : ''}`}
                  name="price"
                  placeholder="0"
                  value={form.price}
                  onChange={handleChange}
                  type="number"
                  min="0"
                />
                {errors.price && <span className="error-text">{errors.price}</span>}
              </div>

              <div className="form-group" style={{ flex: 1 }}>
                <label className="label">Quantity</label>
                <input
                  className="input"
                  name="quantity"
                  placeholder="1"
                  value={form.quantity}
                  onChange={handleChange}
                  type="number"
                  min="1"
                />
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="btn btn-primary btn-full btn-lg"
              disabled={isLoading}
              style={{ marginTop: 8 }}
            >
              {isLoading
                ? <><div className="spinner" />
                    {uploading ? 'Uploading photos...' : 'Creating listing...'}
                  </>
                : <><Upload size={17} /> Publish Listing</>
              }
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}